// War subgame for Classic Card Games.
// Adapted from the standalone war.ts — logic unchanged, imports rngStep from cards.ts.

import { rngStep } from './cards';

export type WarVariant = 'regular' | 'aces-high';
export type WarPhase = 'playing' | 'revealing' | 'complete';
export type WarActionType = 'play' | 'fold' | 'nextRound';

export interface WarConfig {
  variant: WarVariant;
  winningScore: number;
}

export const DEFAULT_WAR_CONFIG: WarConfig = { variant: 'regular', winningScore: 5 };

export interface PlayerInit {
  id: string;
  name: string;
  isBot?: boolean;
}

export interface WarPlayer {
  id: string;
  name: string;
  score: number;
  card: number;              // 1–13
  action: 'play' | 'fold' | null;
  isBot: boolean;
}

export interface WarRoundResult {
  message: string;
  winnerId: string | null;
  highCard: number | null;
}

export interface WarState {
  config: WarConfig;
  phase: WarPhase;
  round: number;
  players: WarPlayer[];
  winnerId: string | null;
  roundResult: WarRoundResult | null;
  rng: number;
}

export type WarAction =
  | { type: 'play'; playerId: string }
  | { type: 'fold'; playerId: string }
  | { type: 'nextRound' };

export interface WarPlayerView {
  id: string;
  name: string;
  score: number;
  card: number | null;
  action: 'play' | 'fold' | null;
  isBot: boolean;
}

export interface WarView {
  config: WarConfig;
  phase: WarPhase;
  round: number;
  players: WarPlayerView[];
  winnerId: string | null;
  roundResult: WarRoundResult | null;
  viewPlayerId: string | null;
}

// --- Card display ---

const WAR_CARD_NAMES: Record<number, string> = {
  1: 'Ace', 2: '2', 3: '3', 4: '4', 5: '5',
  6: '6', 7: '7', 8: '8', 9: '9', 10: '10',
  11: 'Jack', 12: 'Queen', 13: 'King',
};

export function warCardName(value: number): string {
  return WAR_CARD_NAMES[value] ?? String(value);
}

// --- Internal RNG ---

function dealCard(rng: number): [number, number] {
  const [nextRng, r] = rngStep(rng);
  return [Math.floor(r * 13) + 1, nextRng];
}

// --- Core API ---

export function createGame(config: WarConfig, players: PlayerInit[], seed = Date.now()): WarState {
  let rng = seed | 0;
  const warPlayers: WarPlayer[] = [];
  for (const p of players) {
    let card: number;
    [card, rng] = dealCard(rng);
    warPlayers.push({ id: p.id, name: p.name, score: 0, card, action: null, isBot: p.isBot ?? false });
  }
  return { config, phase: 'playing', round: 1, players: warPlayers, winnerId: null, roundResult: null, rng };
}

export function applyAction(state: WarState, action: WarAction): WarState {
  switch (action.type) {
    case 'play':
    case 'fold':
      return applyPlayerAction(state, action);
    case 'nextRound':
      return applyNextRound(state);
  }
}

export function getPlayerView(state: WarState, playerId: string | null): WarView {
  const players: WarPlayerView[] = state.players.map(p => ({
    ...p,
    card: state.phase === 'playing' && playerId !== null && p.id !== playerId ? null : p.card,
  }));
  return { config: state.config, phase: state.phase, round: state.round, players, winnerId: state.winnerId, roundResult: state.roundResult, viewPlayerId: playerId };
}

export function describe(config: WarConfig): string {
  const variantName = config.variant === 'aces-high' ? 'Aces High' : 'Regular';
  const aceRule = config.variant === 'aces-high'
    ? ' Aces (1) beat every other card regardless of numeric value.'
    : ' Aces count as 1 (lowest card).';
  return [
    `War — ${variantName}`,
    '',
    'Each round, every player is dealt a card (1–13: Ace through King) and must choose to Play or Fold.',
    `Play: the single highest card among those who played wins +1 point; all other players who played lose 1 point.${aceRule}`,
    'Fold: avoid the round but immediately lose 1 point.',
    'Ties (multiple players share the highest card): no points change hands.',
    'All players start at 0 — scores can go negative.',
    `First player to reach ${config.winningScore} points wins.`,
  ].join('\n');
}

export function getValidActions(state: WarState, playerId: string): WarActionType[] {
  const player = state.players.find(p => p.id === playerId);
  if (!player || state.phase !== 'playing' || player.action !== null) return [];
  return ['play', 'fold'];
}

export function getBotAction(player: WarPlayer, _state: WarState): 'play' | 'fold' {
  return player.card <= 4 ? 'fold' : 'play';
}

// --- Internal ---

function applyPlayerAction(state: WarState, action: { type: 'play' | 'fold'; playerId: string }): WarState {
  if (state.phase !== 'playing') return state;
  const idx = state.players.findIndex(p => p.id === action.playerId);
  if (idx === -1 || state.players[idx].action !== null) return state;

  const foldPenalty = action.type === 'fold' ? -1 : 0;
  const players = state.players.map((p, i) =>
    i === idx ? { ...p, action: action.type, score: p.score + foldPenalty } : p
  );

  if (!players.every(p => p.action !== null)) return { ...state, players };
  return resolveRound({ ...state, players });
}

function resolveRound(state: WarState): WarState {
  const playing = state.players.filter(p => p.action === 'play');

  if (playing.length === 0) {
    return { ...state, phase: 'revealing', roundResult: { message: 'Everyone folded! No points awarded.', winnerId: null, highCard: null } };
  }

  const { winners, highCard } = determineWinners(playing, state.config);
  let players: WarPlayer[];
  let roundResult: WarRoundResult;

  if (winners.length === 1) {
    const winner = winners[0];
    players = state.players.map(p => {
      if (p.id === winner.id) return { ...p, score: p.score + 1 };
      if (p.action === 'play') return { ...p, score: p.score - 1 };
      return p;
    });
    const aceNote = state.config.variant === 'aces-high' && highCard === 1 ? ' (Ace wins!)' : '';
    roundResult = { message: `${winner.name} wins with ${warCardName(highCard)}!${aceNote}`, winnerId: winner.id, highCard };
  } else {
    players = state.players;
    roundResult = { message: `Tie with ${warCardName(highCard)}! No points awarded.`, winnerId: null, highCard };
  }

  const gameWinner = players.find(p => p.score >= state.config.winningScore);
  return { ...state, players, phase: gameWinner ? 'complete' : 'revealing', winnerId: gameWinner?.id ?? null, roundResult };
}

function determineWinners(playing: WarPlayer[], config: WarConfig): { winners: WarPlayer[]; highCard: number } {
  if (config.variant === 'aces-high') {
    const aces = playing.filter(p => p.card === 1);
    if (aces.length > 0) return { winners: aces, highCard: 1 };
  }
  const highCard = Math.max(...playing.map(p => p.card));
  return { winners: playing.filter(p => p.card === highCard), highCard };
}

function applyNextRound(state: WarState): WarState {
  if (state.phase !== 'revealing' || state.winnerId !== null) return state;
  let rng = state.rng;
  const players = state.players.map(p => {
    let card: number;
    [card, rng] = dealCard(rng);
    return { ...p, card, action: null };
  });
  return { ...state, phase: 'playing', round: state.round + 1, players, roundResult: null, rng };
}
